import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/esm/extends';
import { forwardRef, useMemo, useRef, useState, useEffect } from 'react';
import { SelectionMenu } from '@paypalcorp/pp-react-menu';
import { CriticalIcon, ChevronUpIcon, ChevronDownIcon } from '@paypalcorp/pp-react-icons';
import { devWarning, utilStyles, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysMotionDurationHover = tokens.sysMotionDurationHover,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysSizeFieldLg = tokens.sysSizeFieldLg,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysColorStructureBorderHighContrast = tokens.sysColorStructureBorderHighContrast,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysTypographyFieldLabelLgLineHeight = tokens.sysTypographyFieldLabelLgLineHeight,
      sysTypographyFieldLabelSmLineHeight = tokens.sysTypographyFieldLabelSmLineHeight,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysColorErrorMain = tokens.sysColorErrorMain;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      ellipsis = _utilStyles.ellipsis;

  var _textStyles = textStyles(tokens),
      textFieldLabelLg = _textStyles.fieldLabelLg,
      textFieldValueSm = _textStyles.fieldValueSm,
      textFieldLabelSm = _textStyles.fieldLabelSm,
      textCaption = _textStyles.caption; //Base styles


  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: dropdown_menu_base;\n    position: relative;\n    text-align: start;\n  "])));
  var label = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: dropdown_menu_label;\n    pointer-events: none; // Better way to fix the cursor: text issue!\n    position: absolute;\n    ", "\n    transition: font-size ", " ease-in-out, top ", " ease-in-out, background-color ", " linear;\n    left: ", ";\n    top: ", ";\n    ", "\n    right: ", ";\n\n    [dir='rtl'] & {\n      right: ", ";\n      left: ", ";\n    }\n  "])), textFieldLabelLg(), sysMotionDurationHover, sysMotionDurationHover, sysMotionDurationHover, sysSpaceMd, math("(".concat(sysSizeFieldLg, " - ").concat(sysTypographyFieldLabelLgLineHeight, ") / 2")), ellipsis, math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm)), sysSpaceMd, math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm)));
  var buttonActive = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: active;\n    ", ";\n  "])), focusStyles.focusOffsetElement);
  var button = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: dropdown_menu_button;\n    ", ";\n    box-sizing: border-box;\n    height: ", ";\n    width: 100%;\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    background-color: ", ";\n    padding-top: ", ";\n    padding-right: ", ";\n    padding-left: ", ";\n    padding-bottom: ", ";\n    cursor: pointer;\n    text-align: start;\n    ", "\n\n    &:focus {\n      ", ";\n    }\n\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), textFieldValueSm(), sysSizeFieldLg, sysBorderThicknessNormal, sysColorStructureBorderHighContrast, sysBorderRadiusSm, sysColorBackgroundSurface, math("".concat(sysSpaceSm, " + ").concat(sysSpaceTiny, " + ").concat(sysTypographyFieldLabelSmLineHeight, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)), ellipsis, buttonActive, math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessNormal)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessNormal)));
  var buttonError = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: error;\n    border: ", " solid ", ";\n    padding-top: ", ";\n    padding-right: ", ";\n    padding-left: ", ";\n    padding-bottom: ", ";\n\n    [dir='rtl'] & {\n      padding-left: ", ";\n      padding-right: ", ";\n    }\n  "])), sysBorderThicknessEmphasis, sysColorErrorMain, math("".concat(sysSpaceSm, " + ").concat(sysSpaceTiny, " + ").concat(sysTypographyFieldLabelSmLineHeight, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)));
  var labelActive = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: active;\n    ", "\n    top: ", ";\n  "])), textFieldLabelSm(), sysSpaceSm);
  var affordanceIcon = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: dropdown_menu_affordance_icon;\n    pointer-events: none;\n    position: absolute;\n    top: ", ";\n    right: ", ";\n    margin-left: ", ";\n\n    [dir='rtl'] & {\n      margin-right: ", ";\n      left: ", ";\n      right: auto;\n    }\n  "])), math("".concat(sysSpaceSm, " + ").concat(sysSpaceMd)), math("".concat(sysSpaceSm, " + ").concat(sysSpaceMd)), sysSpaceMd, sysSpaceMd, sysSpaceSm);
  var helperText = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: dropdown_menu_helper_text;\n    ", "\n    margin-top: ", ";\n    list-style: none; // In case it's a list of helper texts\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceXs);
  var errorText = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: dropdown_menu_error_text;\n    ", "\n    display: flex;\n    margin-top: ", ";\n    align-items: center;\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceXs);
  var errorIcon = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: dropdown_menu_error_icon;\n    color: ", ";\n    margin-right: ", ";\n\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysColorErrorMain, sysSpaceXs, sysSpaceXs);
  var selectBox = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: dropdown_menu_select;\n    opacity: 0;\n    position: absolute;\n    pointer-events: none;\n    width: 0;\n    height: 0;\n  "])));
  return {
    base: base,
    label: label,
    button: button,
    buttonError: buttonError,
    buttonActive: buttonActive,
    affordanceIcon: affordanceIcon,
    labelActive: labelActive,
    helperText: helperText,
    errorText: errorText,
    errorIcon: errorIcon,
    selectBox: selectBox
  };
}

var _excluded = ["className", "menuContainerClassName", "menuListClassName", "menuItemClassName", "menuMobileHeaderClassName", "menuButtonClassName", "menuLabelClassName", "skipButtonFocus", "sheetClassName", "sheetWrapperClassName", "id", "label", "name", "errorText", "errorIconAriaLabel", "helperText", "value", "options", "ariaLabelledby", "renderButtonContent", "onChange", "onClose", "onClick", "onKeyDown", "isLoadingOptions", "showUnselect"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var getRefCurrent = function (ref) {
  return (
    /* istanbul ignore next */
    ref && ref.current ? ref.current : null
  );
}; // Get either the error message or helper text or... nothing


var renderMessage = function (errorText, errorIconAriaLabel, helperText, msgId, _styles) {
  if (errorText) {
    var errorProps = {
      css: _styles.errorIcon,
      role: 'img',
      'aria-label': errorIconAriaLabel
    };
    var dropdownErrorIcon = jsx(CriticalIcon, _extends({
      size: "sm"
    }, errorProps, {
      "data-ppui": "true"
    }));
    return jsx("div", {
      css: _styles.errorText,
      id: msgId,
      role: "alert",
      "data-ppui": "true"
    }, dropdownErrorIcon, jsx("span", {
      "data-ppui": "true"
    }, errorText));
  }

  if (helperText) {
    return jsx("div", {
      css: _styles.helperText,
      id: msgId,
      "data-ppui": "true"
    }, jsx("span", {
      "data-ppui": "true"
    }, helperText));
  }

  return null;
};

var hasNoModifiers = function (evt) {
  return !evt.altKey && !evt.ctrlKey && !evt.shiftKey && !evt.metaKey;
};

var DropdownMenu = forwardRef( // eslint-disable-next-line complexity
function (props, ref) {
  var className = props.className,
      menuContainerClassName = props.menuContainerClassName,
      menuListClassName = props.menuListClassName,
      menuItemClassName = props.menuItemClassName,
      menuMobileHeaderClassName = props.menuMobileHeaderClassName,
      menuButtonClassName = props.menuButtonClassName,
      menuLabelClassName = props.menuLabelClassName,
      _props$skipButtonFocu = props.skipButtonFocus,
      skipButtonFocus = _props$skipButtonFocu === void 0 ? false : _props$skipButtonFocu,
      sheetClassName = props.sheetClassName,
      sheetWrapperClassName = props.sheetWrapperClassName,
      id = props.id,
      _props$label = props.label,
      label = _props$label === void 0 ? 'Label' : _props$label,
      name = props.name,
      errorText = props.errorText,
      _props$errorIconAriaL = props.errorIconAriaLabel,
      errorIconAriaLabel = _props$errorIconAriaL === void 0 ? 'Error' : _props$errorIconAriaL,
      helperText = props.helperText,
      value = props.value,
      _props$options = props.options,
      options = _props$options === void 0 ? [] : _props$options,
      ariaLabelledby = props.ariaLabelledby,
      renderButtonContent = props.renderButtonContent,
      onChange = props.onChange,
      onClose = props.onClose,
      onClick = props.onClick,
      onKeyDown = props.onKeyDown,
      isLoadingOptions = props.isLoadingOptions,
      showUnselect = props.showUnselect,
      rest = _objectWithoutProperties(props, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var buttonId = id ? "dropdownMenuButton_".concat(id) : "dropdownMenuButton_".concat(label.replace(/\s/g, ''));
  var internalRef = useRef();
  var buttonRef = ref !== null && ref !== void 0 ? ref : internalRef;
  var selectId = id ? "dropdownMenuSelect_".concat(id) : "dropdownMenuSelect_".concat(label.replace(/\s/g, ''));

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showMenu = _useState2[0],
      setShowMenu = _useState2[1];

  var _useState3 = useState(!!value),
      _useState4 = _slicedToArray(_useState3, 2),
      initialItemFocus = _useState4[0],
      setKeyboardActive = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isRtl = _useState6[0],
      setRtl = _useState6[1];

  useEffect(function () {
    var _window;

    var btn = getRefCurrent(buttonRef);
    var computedStyle = btn && ((_window = window) === null || _window === void 0 ? void 0 : _window.getComputedStyle(btn));
    setRtl((computedStyle === null || computedStyle === void 0 ? void 0 : computedStyle.direction) === 'rtl');
  });

  var openMenu = function (evt) {
    setShowMenu(function (prevState) {
      return !prevState;
    });
    setKeyboardActive(!evt.detail);
  };

  var handleItemClick = function (index) {
    setShowMenu(false);
    var buttonCurrent = getRefCurrent(buttonRef);
    /* istanbul ignore next */

    if (buttonCurrent) {
      if (skipButtonFocus) {
        buttonCurrent.blur();
      } else {
        buttonCurrent.focus();
      }
    }

    var selectedValue;
    /* istanbul ignore next */

    if (onChange) {
      var hasOptionsIndex = options.length > 0 && index >= 0;
      selectedValue = hasOptionsIndex ? options[index].value || options[index].primaryText : null;

      if (showUnselect) {
        selectedValue = hasOptionsIndex ? options[index].value : null;
      }

      var myEvent = {
        target: {
          selectedIndex: index,
          value: selectedValue
        }
      };
      onChange(myEvent);
    }
  };

  var msgId = helperText && 'dropdown_helperText' || errorText && 'dropdown_errorText';
  var buttonText = '';
  var buttonValue = '';
  var selectedIndex = -1;

  if (value) {
    for (var i = 0; i < options.length; i++) {
      if (options[i].value === value) {
        if (renderButtonContent) {
          buttonText = renderButtonContent();
        } else {
          buttonText = options[i].primaryText;
        }

        buttonValue = options[i].value;
        selectedIndex = i;
        break;
      }
    }
  } else if (!value && options.length > 0 && options[0].value === '') {
    selectedIndex = 0;
  }

  var AffordanceIconComponent = showMenu ? ChevronUpIcon : ChevronDownIcon;
  var AffordanceIcon = jsx(AffordanceIconComponent, {
    size: "sm",
    css: _styles.affordanceIcon,
    color: "BackgroundMediumContrast",
    "aria-hidden": "true",
    "data-ppui": "true"
  });
  var labelledBy = ariaLabelledby ? "".concat(buttonId, "-label ").concat(ariaLabelledby) : "".concat(buttonId, "-label");

  if (buttonText) {
    labelledBy = "".concat(labelledBy, " ").concat(buttonId);
  }

  var otherButtonAttr = _objectSpread(_objectSpread(_objectSpread({}, msgId && {
    'aria-describedby': msgId
  }), {
    'aria-labelledby': labelledBy
  }), rest);

  var buttonCssVal = [_styles.button, showMenu && _styles.buttonActive, errorText && _styles.buttonError];
  var labelCssVal = [_styles.label, (showMenu || buttonText) && _styles.labelActive];
  return jsx("div", {
    className: className,
    css: _styles.base,
    id: id,
    "data-ppui-info": "dropdown-menu_3.6.12"
  }, jsx("select", {
    css: _styles.selectBox,
    "aria-hidden": "true",
    id: selectId,
    name: name,
    tabIndex: -1,
    onChange: function (evt) {
      return handleItemClick(evt.target.selectedIndex - 1);
    },
    value: buttonValue,
    "data-ppui": "true"
  }, jsx("option", {
    disabled: true,
    value: "",
    "data-ppui": "true"
  }, label), options.length > 0 && options.map(function (opt, index) {
    return jsx("option", {
      key: index,
      value: opt.value ? opt.value : opt.primaryText,
      "data-ppui": "true"
    }, opt.primaryText);
  })), jsx("button", _extends({
    className: menuButtonClassName,
    css: buttonCssVal,
    type: "button",
    id: buttonId,
    ref: buttonRef,
    "aria-haspopup": "listbox",
    "aria-expanded": showMenu,
    onClick: function handleClick(evt) {
      if (!(options === null || options === void 0 ? void 0 : options.length) && !isLoadingOptions) {
        return;
      }

      openMenu(evt);
      /* istanbul ignore next */

      if (onClick) {
        onClick(evt);
      }
    },
    onKeyDown: function handleKey(evt) {
      /* istanbul ignore next */
      var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

      if (!showMenu && (KeyCode.IS_ARROW_UP || KeyCode.IS_ARROW_DOWN) && hasNoModifiers(evt)) {
        evt.preventDefault();
        openMenu(evt);
      }
      /* istanbul ignore next */


      if (onKeyDown) {
        onKeyDown(evt);
      }
    }
  }, otherButtonAttr, {
    "data-ppui": "true"
  }), AffordanceIcon, jsx("span", {
    "data-ppui": "true"
  }, buttonText)), jsx("label", {
    id: "".concat(buttonId, "-label"),
    htmlFor: buttonId,
    css: labelCssVal,
    className: menuLabelClassName,
    "data-ppui": "true"
  }, jsx("span", {
    "data-ppui": "true"
  }, label)), jsx(SelectionMenu, {
    initialItemFocus: initialItemFocus,
    isOpen: showMenu,
    mobileTitle: label,
    onClose: function handleClose(evt) {
      setShowMenu(false);

      if (onClose) {
        onClose(evt);
      }
    },
    onSelection: handleItemClick,
    selectedIndex: selectedIndex,
    triggerElementRef: buttonRef,
    options: options,
    className: menuListClassName,
    containerClassName: menuContainerClassName,
    itemClassName: menuItemClassName,
    mobileHeaderClassName: menuMobileHeaderClassName,
    sheetClassName: sheetClassName,
    sheetWrapperClassName: sheetWrapperClassName,
    rtl: isRtl,
    returnFocusToTrigger: !skipButtonFocus,
    isLoadingOptions: isLoadingOptions,
    searchEnabled: true,
    "data-ppui": "true"
  }), renderMessage(errorText, errorIconAriaLabel, helperText, msgId, _styles));
});

export { DropdownMenu, styles as dropdownMenuStyles };
